/**
 * AccessRequestLocationPicker
 *
 * Standalone location-picker for the Access Request form.
 * Supports three picker modes driven by role scope:
 *   • "site"   – up to N sites selected via Region > Market > Site tree
 *   • "market" – one or more markets selected via Region > Market tree
 *   • "region" – one or more regions selected via Region tree
 *
 * All data is static / mock.  Wire up `onSelectionChange` to persist selections.
 */
import React, { useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

// ── Static location tree (mock) ───────────────────────────────────────────────

type LocationSite = { name: string };
type LocationMarket = { market: string; siteCount: number; sites: LocationSite[] };
export type LocationRegion = { region: string; siteCount: number; markets: LocationMarket[] };

// Static LOCATION_TREE removed — picker now relies solely on the
// `locationTree` prop (dynamic data from the API). Do not reintroduce
// static mock data here; returning an empty tree renders the "no locations"
// banner when the API has no matches.

// ── Types ─────────────────────────────────────────────────────────────────────

export type LocationPickerType = "site" | "market" | "region";

type Props = {
  pickerType: LocationPickerType;
  /** When "1NA" and pickerType is market/site, show the "no locations" error banner */
  organization?: string;
  /** Max selectable items when pickerType = "site" (default 5) */
  maxSiteSelections?: number;
  /** Max distinct regions selectable when pickerType = "market" (default: unlimited) */
  maxRegionSelections?: number;
  selectedItems: string[];
  onSelectionChange: (selected: string[]) => void;
  /** Optional live location tree from Redux (no static fallback; pass [] when no matches) */
  locationTree?: LocationRegion[];
  /** When true, show the selection state but disable edits */
  selectionLocked?: boolean;
};

// ── Helper ────────────────────────────────────────────────────────────────────

const getInitials = (s: string) =>
  s
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();

const normalizeLocationLabel = (value: string | null | undefined): string => {
  const trimmed = String(value ?? "").trim();
  return trimmed || "MFG";
};

// ── Component ─────────────────────────────────────────────────────────────────

const AccessRequestLocationPicker: React.FC<Props> = ({
  pickerType,
  organization,
  maxSiteSelections = 5,
  maxRegionSelections,
  selectedItems,
  onSelectionChange,
  locationTree: locationTreeProp,
  selectionLocked = false,
}) => {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [expandedMarkets, setExpandedMarkets] = useState<Set<string>>(new Set());

  // Use live tree from props when provided. Do NOT fall back to the static
  // LOCATION_TREE — if the caller doesn't provide a tree, treat it as an
  // empty tree so only dynamic data is shown.
  const TREE = React.useMemo(
    () =>
      (locationTreeProp ?? []).map((region) => ({
        ...region,
        region: normalizeLocationLabel(region.region),
        markets: (region.markets || []).map((market) => ({
          ...market,
          market: normalizeLocationLabel(market.market),
          sites: (market.sites || []).map((site) => ({
            ...site,
            name: normalizeLocationLabel(site.name),
          })),
        })),
      })),
    [locationTreeProp]
  );

  const fullAccessSelections = React.useMemo(
    () =>
      Array.from(
        new Set(
          TREE.flatMap((reg) => reg.markets.flatMap((mkt) => mkt.sites.map((site) => site.name)))
        )
      ),
    [TREE]
  );

  const effectiveSelectedItems = selectionLocked ? fullAccessSelections : selectedItems;

  // Treat an explicitly-provided empty tree as "no locations" so callers can
  // indicate there are no matches from the API. Also preserve the existing
  // 1NA/org rule for non-region pickers.
  const explicitEmptyTree = locationTreeProp !== undefined && locationTreeProp.length === 0;
  const noLocations = explicitEmptyTree || (organization === "1NA" && pickerType !== "region");

  const toggleRegion = (region: string) =>
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      next.has(region) ? next.delete(region) : next.add(region);
      return next;
    });

  const toggleMarket = (market: string) =>
    setExpandedMarkets((prev) => {
      const next = new Set(prev);
      next.has(market) ? next.delete(market) : next.add(market);
      return next;
    });

  const toggleSite = (site: string) => {
    if (selectedItems.includes(site)) {
      onSelectionChange(selectedItems.filter((s) => s !== site));
    } else if (selectedItems.length < maxSiteSelections) {
      onSelectionChange([...selectedItems, site]);
    }
  };

  const toggleRegionSel = (region: string) =>
    selectionLocked
      ? undefined
      :
    onSelectionChange(
      selectedItems.includes(region)
        ? selectedItems.filter((r) => r !== region)
        : [...selectedItems, region]
    );

  const toggleMarketSel = (market: string) =>
    selectionLocked
      ? undefined
      :
    onSelectionChange(
      selectedItems.includes(market)
        ? selectedItems.filter((m) => m !== market)
        : [...selectedItems, market]
    );

  const getMarketSites = (market: string): string[] =>
    TREE.flatMap((reg) => reg.markets)
      .find((mkt) => mkt.market === market)
      ?.sites.map((s) => s.name) ?? [];

  const isMarketLegacySelected = (market: string) => selectedItems.includes(market);

  const getSelectedSitesInMarket = (market: string): number => {
    const marketSites = getMarketSites(market);
    if (marketSites.length === 0) {
      return 0;
    }
    if (isMarketLegacySelected(market)) {
      return marketSites.length;
    }
    return marketSites.filter((site) => selectedItems.includes(site)).length;
  };

  const getMarketRegion = (market: string): string | undefined =>
    TREE.find((reg) => reg.markets.some((mkt) => mkt.market === market))?.region;

  /** Distinct regions currently represented by selected markets/sites (market pickerType only). */
  const getSelectedRegionsForMarkets = (): Set<string> =>
    new Set(
      TREE.filter((reg) => reg.markets.some((mkt) => getSelectedSitesInMarket(mkt.market) > 0)).map(
        (reg) => reg.region
      )
    );

  /** True when selecting something in `market` would introduce a new region beyond the cap. */
  const isNewRegionBlocked = (market: string): boolean => {
    if (maxRegionSelections == null) {
      return false;
    }
    const region = getMarketRegion(market);
    if (!region) {
      return false;
    }
    const selectedRegions = getSelectedRegionsForMarkets();
    return !selectedRegions.has(region) && selectedRegions.size >= maxRegionSelections;
  };

  const toggleMarketSelectAllSites = (market: string) => {
    if (selectionLocked) {
      return;
    }

    const marketSites = getMarketSites(market);
    const selectedInMarket = getSelectedSitesInMarket(market);
    const fullySelected = marketSites.length > 0 && selectedInMarket === marketSites.length;

    if (fullySelected) {
      onSelectionChange(
        selectedItems.filter((item) => item !== market && !marketSites.includes(item))
      );
      return;
    }

    if (isNewRegionBlocked(market)) {
      return;
    }

    const withoutLegacyMarketToken = selectedItems.filter((item) => item !== market);
    const merged = new Set([...withoutLegacyMarketToken, ...marketSites]);
    onSelectionChange(Array.from(merged));
  };

  const toggleMarketSite = (market: string, site: string) => {
    if (selectionLocked) {
      return;
    }

    const marketSites = getMarketSites(market);

    // Backward compatibility: legacy payloads may store market names directly.
    const baseSelected = isMarketLegacySelected(market)
      ? [
          ...selectedItems.filter((item) => item !== market),
          ...marketSites,
        ]
      : [...selectedItems];

    if (!baseSelected.includes(site) && isNewRegionBlocked(market)) {
      return;
    }

    const next = new Set(baseSelected);
    if (next.has(site)) {
      next.delete(site);
    } else {
      next.add(site);
    }
    onSelectionChange(Array.from(next));
  };

  // ── No-locations banner ──────────────────────────────────────────────────────
  if (noLocations) {
    return (
      <View style={lp.sectionWrap}>
        <Text style={lp.sectionTitle}>Location</Text>
        <View style={lp.noLocBanner}>
          <MaterialIcons name="warning" size={18} color="#c12c01" style={{ marginTop: 1 }} />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={lp.noLocTitle}>No locations match that criteria</Text>
            <Text style={lp.noLocDesc}>
              No eligible sites are loaded in Location Management for this organization and
              department yet.
            </Text>
          </View>
        </View>
      </View>
    );
  }

  const selectedCount = effectiveSelectedItems.length;
  const totalMarkets = TREE.flatMap((reg) => reg.markets).length;
  const totalSites = TREE.flatMap((reg) => reg.markets).reduce(
    (count, market) => count + market.sites.length,
    0
  );

  // ── Region-level picker ──────────────────────────────────────────────────────
  if (pickerType === "region") {
    return (
      <View style={lp.sectionWrap}>
        <View style={lp.sectionHeaderRow}>
          <View>
            <Text style={lp.sectionTitle}>Location</Text>
            <Text style={lp.subHeader}>Request access - one or more regions</Text>
          </View>
          <Text style={lp.counterText}>{selectedCount} selected</Text>
        </View>

        <View style={lp.table}>
          {/* Table head */}
          <View style={lp.tableHead}>
            <Text style={[lp.thCell, { flex: 1 }]}>REGION - HIERARCHY</Text>
            <Text style={[lp.thCell, { width: 240 }]}>WHAT THIS COVERS</Text>
            <Text style={[lp.thCell, { width: 100, textAlign: "right" }]}>STATUS</Text>
          </View>

          {TREE.map((reg) => {
            const isSelected = selectedItems.includes(reg.region);
            const isExpanded = expandedRegions.has(reg.region);
            return (
              <React.Fragment key={reg.region}>
                {/* Region row */}
                <View style={lp.tableRow}>
                  <Pressable onPress={() => toggleRegion(reg.region)} style={lp.chevronBtn}>
                    <MaterialIcons
                      name={isExpanded ? "expand-more" : "chevron-right"}
                      size={18}
                      color="#585858"
                    />
                  </Pressable>
                  <Pressable
                    onPress={selectionLocked ? undefined : () => toggleRegionSel(reg.region)}
                    style={lp.checkboxBtn}
                  >
                    <View style={[lp.checkbox, isSelected && lp.checkboxChecked, selectionLocked && lp.checkboxDisabled]}>
                      {isSelected && (
                        <MaterialIcons name="check" size={11} color="#ffffff" />
                      )}
                    </View>
                  </Pressable>
                  <Pressable onPress={selectionLocked ? undefined : () => toggleRegionSel(reg.region)} style={{ flex: 1 }}>
                    <Text style={[lp.regionName, isSelected && { color: "#005cbc" }]}>
                      {reg.region}{" "}
                      <Text style={lp.siteCountText}>({reg.siteCount} sites)</Text>
                    </Text>
                  </Pressable>
                  <Text style={[lp.coverageText, { width: 240 }]}>
                    All {reg.siteCount} sites in this region
                  </Text>
                  <View style={[lp.statusCell, { width: 100 }]}>
                    {isSelected ? (
                      <View style={lp.badgeRequested}>
                        <Text style={lp.badgeRequestedText}>Requested</Text>
                      </View>
                    ) : (
                      <Text style={lp.statusDash}>-</Text>
                    )}
                  </View>
                </View>

                {/* Expanded: markets */}
                {isExpanded &&
                  reg.markets.map((mkt) => {
                    const mktExpanded = expandedMarkets.has(mkt.market);
                    return (
                      <React.Fragment key={mkt.market}>
                        <View style={[lp.tableRow, lp.indent1]}>
                          <Pressable onPress={() => toggleMarket(mkt.market)} style={lp.chevronBtn}>
                            <MaterialIcons
                              name={mktExpanded ? "expand-more" : "chevron-right"}
                              size={16}
                              color="#585858"
                            />
                          </Pressable>
                          <View style={{ flex: 1 }}>
                            <Text style={lp.marketName}>{mkt.market}</Text>
                          </View>
                          <Text style={[lp.coverageText, { width: 240 }]}>
                            Included with region
                          </Text>
                          <View style={[lp.statusCell, { width: 100 }]}>
                            {isSelected ? (
                              <View style={lp.badgeIncluded}>
                                <Text style={lp.badgeIncludedText}>Included</Text>
                              </View>
                            ) : (
                              <Text style={lp.statusDash}>-</Text>
                            )}
                          </View>
                        </View>

                        {/* Expanded: sites */}
                        {mktExpanded &&
                          mkt.sites.map((site) => (
                            <View key={site.name} style={[lp.tableRow, lp.indent2]}>
                              <View style={{ flex: 1 }}>
                                <Text style={lp.siteName}>{site.name}</Text>
                              </View>
                              <Text style={[lp.coverageText, { width: 240 }]}>Included</Text>
                              <View style={[lp.statusCell, { width: 100 }]}>
                                {isSelected ? (
                                  <View style={lp.badgeIncluded}>
                                    <Text style={lp.badgeIncludedText}>Included</Text>
                                  </View>
                                ) : (
                                  <Text style={lp.statusDash}>-</Text>
                                )}
                              </View>
                            </View>
                          ))}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </View>

        <View style={lp.footerRow}>
          <MaterialIcons name="info-outline" size={13} color="#585858" />
          <Text style={lp.footerNote}>
            Pick <Text style={{ fontWeight: "700" }}>one or more regions</Text> - each covers
            every market and site inside it. Expand to preview what's included.
          </Text>
        </View>
      </View>
    );
  }

  // ── Market-level picker ──────────────────────────────────────────────────────
  if (pickerType === "market") {
    const selectedMarketCount = TREE.flatMap((reg) => reg.markets).filter((mkt) =>
      selectedItems.includes(mkt.market)
    ).length;
    const selectedRegionsForMarketPicker = getSelectedRegionsForMarkets();

    return (
      <View style={lp.sectionWrap}>
        <View style={lp.sectionHeaderRow}>
          <View>
            <Text style={lp.sectionTitle}>Location</Text>
            <Text style={lp.subHeader}>
              {maxRegionSelections != null
                ? `Request access - one or more markets across up to ${maxRegionSelections} regions`
                : "Request access - one or more markets"}
            </Text>
          </View>
          <Text style={lp.counterText}>
            {selectedMarketCount} selected
            {maxRegionSelections != null
              ? ` - ${selectedRegionsForMarketPicker.size}/${maxRegionSelections} regions`
              : ""}
          </Text>
        </View>

        <View style={lp.table}>
          <View style={lp.tableHead}>
            <Text style={[lp.thCell, { flex: 1 }]}>REGION - MARKET</Text>
            <Text style={[lp.thCell, { width: 240 }]}>WHAT THIS COVERS</Text>
            <Text style={[lp.thCell, { width: 100, textAlign: "right" }]}>STATUS</Text>
          </View>

          {TREE.map((reg) => {
            const isRegExpanded = expandedRegions.has(reg.region);
            const selectedMarketsInRegion = reg.markets.filter(
              (mkt) => getSelectedSitesInMarket(mkt.market) > 0
            ).length;
            const isRegionBlocked =
              maxRegionSelections != null &&
              !selectedRegionsForMarketPicker.has(reg.region) &&
              selectedRegionsForMarketPicker.size >= maxRegionSelections;
            return (
              <React.Fragment key={reg.region}>
                <View style={lp.tableRow}>
                  <Pressable onPress={() => toggleRegion(reg.region)} style={lp.chevronBtn}>
                    <MaterialIcons
                      name={isRegExpanded ? "expand-more" : "chevron-right"}
                      size={18}
                      color="#585858"
                    />
                  </Pressable>
                  <View style={{ flex: 1 }}>
                    <Text style={[lp.regionName, isRegionBlocked && { color: "#b0b0b0" }]}>
                      {reg.region}{" "}
                      <Text style={lp.siteCountText}>({reg.siteCount} sites)</Text>
                    </Text>
                  </View>
                  <Text style={[lp.coverageText, { width: 240 }]}>
                    {reg.siteCount} sites - expand to view markets
                  </Text>
                  <View style={[lp.statusCell, { width: 100 }]}>
                    {selectedMarketsInRegion > 0 ? (
                      <View style={lp.badgeCount}>
                        <Text style={lp.badgeCountText}>
                          {selectedMarketsInRegion} / {reg.markets.length}
                        </Text>
                      </View>
                    ) : (
                      <Text style={lp.statusDash}>-</Text>
                    )}
                  </View>
                </View>

                {isRegExpanded &&
                  reg.markets.map((mkt) => {
                    const isMktSelected = selectedItems.includes(mkt.market);
                    const mktExpanded = expandedMarkets.has(mkt.market);
                    return (
                      <React.Fragment key={mkt.market}>
                        <View style={[lp.tableRow, lp.indent1]}>
                          <Pressable onPress={() => toggleMarket(mkt.market)} style={lp.chevronBtn}>
                            <MaterialIcons
                              name={mktExpanded ? "expand-more" : "chevron-right"}
                              size={16}
                              color="#585858"
                            />
                          </Pressable>
                          <Pressable
                            onPress={() => !isRegionBlocked && toggleMarketSelectAllSites(mkt.market)}
                            style={lp.checkboxBtn}
                            disabled={isRegionBlocked && !isMktSelected}
                          >
                            <View style={[lp.checkbox, isMktSelected && lp.checkboxChecked, isRegionBlocked && !isMktSelected && lp.checkboxDisabled]}>
                              {isMktSelected && (
                                <MaterialIcons name="check" size={11} color="#ffffff" />
                              )}
                            </View>
                          </Pressable>
                          <Pressable
                            onPress={() => !isRegionBlocked && toggleMarketSelectAllSites(mkt.market)}
                            style={{ flex: 1 }}
                            disabled={isRegionBlocked && !isMktSelected}
                          >
                            <Text
                              style={[
                                lp.marketName,
                                isMktSelected && { color: "#005cbc", fontWeight: "700" },
                                isRegionBlocked && !isMktSelected && { color: "#b0b0b0" },
                              ]}
                            >
                              {mkt.market}
                            </Text>
                          </Pressable>
                          <Text style={[lp.coverageText, { width: 240 }]}>
                            All locations within this market
                          </Text>
                          <View style={[lp.statusCell, { width: 100 }]}>
                            {isMktSelected ? (
                              <View style={lp.badgeRequested}>
                                <Text style={lp.badgeRequestedText}>Requested</Text>
                              </View>
                            ) : (
                              <Text style={lp.statusDash}>-</Text>
                            )}
                          </View>
                        </View>

                        {mktExpanded &&
                          mkt.sites.map((site) => {
                            const isSiteSelected =
                              isMarketLegacySelected(mkt.market) || selectedItems.includes(site.name);
                            const isSiteBlocked = isRegionBlocked && !isSiteSelected;
                            return (
                              <View key={site.name} style={[lp.tableRow, lp.indent2]}>
                                <Pressable
                                  onPress={() => !isSiteBlocked && toggleMarketSite(mkt.market, site.name)}
                                  style={lp.checkboxBtn}
                                  disabled={isSiteBlocked}
                                >
                                  <View style={[lp.checkbox, isSiteSelected && lp.checkboxChecked, isSiteBlocked && lp.checkboxDisabled]}>
                                    {isSiteSelected && (
                                      <MaterialIcons name="check" size={11} color="#ffffff" />
                                    )}
                                  </View>
                                </Pressable>
                                <Pressable
                                  onPress={() => !isSiteBlocked && toggleMarketSite(mkt.market, site.name)}
                                  style={{ flex: 1 }}
                                  disabled={isSiteBlocked}
                                >
                                  <Text style={[lp.siteName, isSiteSelected && { color: "#005cbc" }, isSiteBlocked && { color: "#b0b0b0" }]}> 
                                    {site.name}
                                  </Text>
                                </Pressable>
                                <Text style={[lp.coverageText, { width: 240 }]}> 
                                  Assigned site - field execution views
                                </Text>
                                <View style={[lp.statusCell, { width: 100 }]}> 
                                  {isMktSelected ? (
                                    <View style={lp.badgeRequested}>
                                      <Text style={lp.badgeIncludedText}>Included</Text>
                                    </View>
                                  ) : (
                                    <Text style={lp.statusDash}>-</Text>
                                  )}
                                </View>
                              </View>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </View>

        <View style={lp.footerRow}>
          <MaterialIcons name="info-outline" size={13} color="#585858" />
          <Text style={lp.footerNote}>
            Pick <Text style={{ fontWeight: "700" }}>one or more markets</Text> - each covers every
            site inside it. Expand to preview sites.
            {maxRegionSelections != null
              ? ` Markets can span up to ${maxRegionSelections} regions.`
              : ""}
          </Text>
        </View>
      </View>
    );
  }

  // ── Site-level picker (default) ──────────────────────────────────────────────
  return (
    <View style={lp.sectionWrap}>
      <View style={lp.sectionHeaderRow}>
        <View>
          <Text style={lp.sectionTitle}>Location</Text>
          {selectionLocked ? null : (
            <Text style={lp.subHeader}>
              Request access - up to {maxSiteSelections} sites
            </Text>
          )}
        </View>
        {selectionLocked ? null : (
          <Text style={lp.counterText}>
            {selectedCount} / {maxSiteSelections} selected
          </Text>
        )}
      </View>

      {selectionLocked ? (
        <View style={lp.fullAccessBanner}>
          <MaterialIcons name="shield" size={16} color="#1f6d1f" style={{ marginTop: 1 }} />
          <Text style={lp.fullAccessBannerText}>
            <Text style={{ fontWeight: "700" }}>All locations selected</Text> - {TREE.length} regions - {totalMarkets} markets - {totalSites} sites. This role always receives every location, so there&apos;s nothing to choose.
          </Text>
        </View>
      ) : null}

      <View style={lp.table}>
        <View style={lp.tableHead}>
          <Text style={[lp.thCell, { flex: 1 }]}>REGION - MARKET - SITE</Text>
          <Text style={[lp.thCell, { width: 240 }]}>COVERAGE</Text>
          <Text style={[lp.thCell, { width: 100, textAlign: "right" }]}>STATUS</Text>
        </View>

        {TREE.map((reg) => {
          const isRegExpanded = expandedRegions.has(reg.region);
          const allRegSites = reg.markets.flatMap((m) => m.sites.map((s) => s.name));
          const selInReg = allRegSites.filter((s) => effectiveSelectedItems.includes(s)).length;
          const isRegSelected = selInReg > 0 && selInReg === allRegSites.length;

          return (
            <React.Fragment key={reg.region}>
              <View style={lp.tableRow}>
                <Pressable onPress={() => toggleRegion(reg.region)} style={lp.chevronBtn}>
                  <MaterialIcons
                    name={isRegExpanded ? "expand-more" : "chevron-right"}
                    size={18}
                    color={selInReg > 0 ? "#005cbc" : "#585858"}
                  />
                </Pressable>
                {selectionLocked ? (
                  <Pressable style={lp.checkboxBtn} disabled>
                      <View style={[lp.checkbox, isRegSelected && lp.checkboxChecked, lp.checkboxDisabled]}>
                      {isRegSelected && <MaterialIcons name="check" size={11} color="#9ca3af" />}
                    </View>
                  </Pressable>
                ) : null}
                <View style={{ flex: 1 }}>
                  <Text style={[lp.regionName, selInReg > 0 && { color: "#005cbc" }]}>
                    {reg.region}
                  </Text>
                </View>
                <Text style={[lp.coverageText, { width: 240 }]}>
                  {reg.siteCount} sites - expand to view markets
                </Text>
                <View style={[lp.statusCell, { width: 100 }]}>
                  {selInReg > 0 ? (
                    <View style={lp.badgeCount}>
                      <Text style={lp.badgeCountText}>{selInReg} / {reg.siteCount}</Text>
                    </View>
                  ) : (
                    <Text style={lp.statusDash}>-</Text>
                  )}
                </View>
              </View>

              {isRegExpanded &&
                reg.markets.map((mkt) => {
                  const isMktExpanded = expandedMarkets.has(mkt.market);
                    const selInMkt = mkt.sites.filter((s) =>
                      effectiveSelectedItems.includes(s.name)
                    ).length;
                    const isMktSelected = selInMkt > 0 && selInMkt === mkt.sites.length;

                  return (
                    <React.Fragment key={mkt.market}>
                      <View style={[lp.tableRow, lp.indent1]}>
                        <Pressable onPress={() => toggleMarket(mkt.market)} style={lp.chevronBtn}>
                          <MaterialIcons
                            name={isMktExpanded ? "expand-more" : "chevron-right"}
                            size={16}
                            color={selInMkt > 0 ? "#005cbc" : "#585858"}
                          />
                        </Pressable>
                          {selectionLocked ? (
                            <Pressable style={lp.checkboxBtn} disabled>
                              <View style={[lp.checkbox, isMktSelected && lp.checkboxChecked, lp.checkboxDisabled]}>
                                {isMktSelected && <MaterialIcons name="check" size={11} color="#9ca3af" />}
                              </View>
                            </Pressable>
                          ) : null}
                        <View style={{ flex: 1 }}>
                          <Text style={[lp.marketName, selInMkt > 0 && { color: "#005cbc" }]}>
                            {mkt.market}
                          </Text>
                        </View>
                        <Text style={[lp.coverageText, { width: 240 }]}>
                          {mkt.siteCount} sites - expand to view sites
                        </Text>
                        <View style={[lp.statusCell, { width: 100 }]}>
                          {selInMkt > 0 ? (
                            <View style={lp.badgeCount}>
                              <Text style={lp.badgeCountText}>{selInMkt} / {mkt.siteCount}</Text>
                            </View>
                          ) : (
                            <Text style={lp.statusDash}>-</Text>
                          )}
                        </View>
                      </View>

                      {isMktExpanded &&
                        mkt.sites.map((site) => {
                          const isChecked = effectiveSelectedItems.includes(site.name);
                          const isAtMax = effectiveSelectedItems.length >= maxSiteSelections && !isChecked;
                          return (
                            <View key={site.name} style={[lp.tableRow, lp.indent2]}>
                              <Pressable
                                onPress={selectionLocked ? undefined : () => !isAtMax && toggleSite(site.name)}
                                style={lp.checkboxBtn}
                                disabled={selectionLocked}
                              >
                                <View
                                  style={[
                                    lp.checkbox,
                                    isChecked && lp.checkboxChecked,
                                    (isAtMax || selectionLocked) && lp.checkboxDisabled,
                                  ]}
                                >
                                  {isChecked && (
                                    <MaterialIcons name="check" size={11} color={selectionLocked ? "#9ca3af" : "#ffffff"} />
                                  )}
                                </View>
                              </Pressable>
                              <View style={{ flex: 1 }}>
                                <Text style={[lp.siteName, (isAtMax || selectionLocked) && { color: "#b0b0b0" }]}> 
                                  {site.name}
                                </Text>
                              </View>
                              <Text style={[lp.coverageText, { width: 240 }]}>
                                Assigned site - field execution views
                              </Text>
                              <View style={[lp.statusCell, { width: 100 }]}>
                                {isChecked ? (
                                  <View style={lp.badgeIncluded}>
                                    <Text style={lp.badgeIncludedText}>Included</Text>
                                  </View>
                                ) : (
                                  <Text style={lp.statusDash}>-</Text>
                                )}
                              </View>
                            </View>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          );
        })}
      </View>

      {!selectionLocked ? (
        <View style={lp.footerRow}>
          <MaterialIcons name="info-outline" size={13} color="#585858" />
          <Text style={lp.footerNote}>
            Select up to{" "}
            <Text style={{ fontWeight: "700" }}>{maxSiteSelections} sites</Text>. Expand a region
            or market to drill into its sites - region- and market-level access isn't available to
            this role; it requires a Region or Market Leader role.
          </Text>
        </View>
      ) : null}
    </View>
  );
};

export default AccessRequestLocationPicker;

// ── Styles ────────────────────────────────────────────────────────────────────

const lp = StyleSheet.create({
  sectionWrap: {
    width: "100%",
    marginBottom: 8,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#2d2d2d",
    marginBottom: 2,
  },
  subHeader: {
    fontSize: 12,
    fontWeight: "700",
    color: "#2d2d2d",
    marginTop: 2,
  },
  counterText: {
    fontSize: 12,
    color: "#585858",
    fontWeight: "600",
    marginTop: 4,
  },

  // ── Table ──────────────────────────────────────────────────────────────────
  table: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  tableHead: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f9fafb",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  thCell: {
    fontSize: 11,
    fontWeight: "700",
    color: "#6b7280",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    backgroundColor: "#ffffff",
    minHeight: 44,
  },
  indent1: {
    paddingLeft: 28,
  },
  indent2: {
    paddingLeft: 44,
  },

  // ── Row controls ───────────────────────────────────────────────────────────
  chevronBtn: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 4,
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  checkboxBtn: {
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "#d0d0d0",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: {
    borderColor: "#005cbc",
    backgroundColor: "#005cbc",
  },
  checkboxDisabled: {
    backgroundColor: "#f3f4f6",
    borderColor: "#e5e7eb",
  },

  // ── Row text ───────────────────────────────────────────────────────────────
  regionName: {
    fontSize: 13,
    fontWeight: "700",
    color: "#005cbc",
    ...(Platform.OS === "web" ? { cursor: "pointer" } as any : {}),
  },
  siteCountText: {
    fontSize: 12,
    fontWeight: "400",
    color: "#6b7280",
  },
  marketName: {
    fontSize: 13,
    fontWeight: "600",
    color: "#2d2d2d",
  },
  siteName: {
    fontSize: 13,
    color: "#374151",
  },
  coverageText: {
    fontSize: 12,
    color: "#6b7280",
    fontStyle: "italic",
    paddingLeft: 8,
  },
  statusCell: {
    alignItems: "flex-end",
  },
  statusDash: {
    fontSize: 14,
    color: "#9ca3af",
  },

  // ── Status badges ──────────────────────────────────────────────────────────
  badgeRequested: {
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: "#dbeafe",
  },
  badgeRequestedText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#1d4ed8",
  },
  badgeIncluded: {
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: "#86efac",
    backgroundColor: "#f0fdf4",
  },
  badgeIncludedText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#15803d",
  },
  badgeCount: {
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: "#fef3c7",
  },
  badgeCountText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#8a5a00",
  },

  // ── Footer note ────────────────────────────────────────────────────────────
  footerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    marginTop: 8,
  },

  fullAccessBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#a7f3d0",
    backgroundColor: "#dcfce7",
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 18,
  },
  fullAccessBannerText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    color: "#166534",
  },
  footerNote: {
    flex: 1,
    fontSize: 12,
    color: "#585858",
    lineHeight: 18,
  },

  // ── No-locations banner ────────────────────────────────────────────────────
  noLocBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#fff5f5",
    borderWidth: 1,
    borderColor: "#fecaca",
    borderRadius: 8,
    padding: 14,
  },
  noLocTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#c12c01",
    marginBottom: 3,
  },
  noLocDesc: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 17,
  },
});
